import { useMutation } from "@tanstack/react-query";
import { ScheduledTaskDTO } from "api/model/airflow-task/ScheduledTaskDTO";
import {
  errorToaster,
  successToaster,
  warningToaster,
} from "components/pages/measurement-devices/utils/utils";
import { queryClient } from "services/HttpService";
import { T } from "services/lang/LanguageService";
import {
  AirflowDag,
  AirflowDagDto,
  AirflowSearchQuery,
  AirflowTaskDto,
  AirflowTaskHistoriesPaginatedRequest,
  AirflowTaskId,
} from "types/airflow";
import { IScheduledTaskHistoryDTO, ScheduledTasksApi } from "../ScheduledTasksApi";
import { IQueryOptions, QueryKey, defaultResponseHandler, useQueryWrapper } from "./api-hooks-common";

export const useGetAirflowDagTaskInfos = (dagId: string) =>
  useQueryWrapper([QueryKey.AIRFLOW_DAG_TASK_INFOS, dagId], () =>
    ScheduledTasksApi.getAirflowTaskDataInfos(dagId)
  );

export const useGetAirflowTaskHistories = (
  request: AirflowTaskHistoriesPaginatedRequest,
  options?: IQueryOptions<IScheduledTaskHistoryDTO>
) =>
  useQueryWrapper(
    [
      QueryKey.SCHEDULED_TASK_HISTORIES,
      request.dagId,
      request.taskId,
      request.skip?.toString(),
      request.take?.toString(),
    ],
    () => ScheduledTasksApi.getAirflowTaskHistories(request),
    null,
    options
  );

export const useGetCompanyDags = () =>
  useQueryWrapper([QueryKey.SCHEDULED_TASK_DAGS], () => ScheduledTasksApi.getCompanyDags());

export const useGetTaskTemplates = () =>
  useQueryWrapper([QueryKey.SCHEDULED_TASK_TEMPLATES], () => ScheduledTasksApi.getTaskTemplates());

export const useTriggerAirflowTask = () => {
  return useMutation({
    mutationFn: ({ dagId, taskId }: AirflowTaskId) => ScheduledTasksApi.triggerAirflowTask(dagId, taskId),
    onSuccess() {
      successToaster(T("alert.task-triggered-successfully"));
    },
    onError() {
      errorToaster(T("alert.failed-to-trigger-task"));
    },
  });
};

export const useGetPythonTaskTemplates = (companyRef: string) =>
  useQueryWrapper([QueryKey.SCHEDULED_PYTHON_TASK_TEMPLATES, companyRef], () =>
    ScheduledTasksApi.getPythonTaskTemplates(companyRef)
  );

export const useGetDag = (dagId: string) =>
  useQueryWrapper([QueryKey.SCHEDULED_TASK_DAG, dagId], () => ScheduledTasksApi.getDag(dagId));

export const useCreateDag = () => {
  return useMutation({
    mutationFn: async (dag: AirflowDagDto) => {
      const res = await ScheduledTasksApi.createDag(dag);
      return defaultResponseHandler(res);
    },
    onSuccess(dag) {
      queryClient.setQueryData([QueryKey.SCHEDULED_TASK_DAGS], (oldQueryData: AirflowDagDto[]) => [
        ...oldQueryData,
        dag,
      ]);
      successToaster(T("alert.dag-created-successfully", { dagId: dag?.dagId }));
    },
  });
};

export const useUpdateDag = () => {
  return useMutation({
    mutationFn: async (dag: AirflowDagDto) => {
      const res = await ScheduledTasksApi.updateDag(dag);
      return defaultResponseHandler(res);
    },
    onSuccess(response) {
      queryClient.setQueryData(
        [QueryKey.SCHEDULED_TASK_DAGS],
        (oldQueryData: AirflowDagDto[]) =>
          oldQueryData &&
          oldQueryData.map(oldDag => (oldDag.dagId === response?.dagDto?.dagId ? response.dagDto : oldDag))
      );
      queryClient.setQueryData(
        [QueryKey.SCHEDULED_TASK_DAG, response?.dagDto?.dagId],
        () => response?.dagDto
      );
      if (response && response.deployed) {
        successToaster(T("alert.dag-updated-successfully", { dagId: response?.dagDto?.dagId }));
      } else {
        warningToaster(T("alert.dag-updated-but-not-deployed", { dagId: response?.dagDto?.dagId }));
      }
    },
  });
};

export const useDeleteDag = () => {
  return useMutation({
    mutationFn: async (dagId: string) => {
      const res = await ScheduledTasksApi.deleteDag(dagId);
      return defaultResponseHandler(res);
    },
    onSuccess(_, dagId) {
      queryClient.setQueryData([QueryKey.SCHEDULED_TASK_DAGS], (oldQueryData: AirflowDagDto[]) =>
        oldQueryData.filter(dag => dag.dagId !== dagId)
      );
      successToaster(T("alert.dag-deleted-successfully", { dagId }));
    },
  });
};

export const useCreateTask = () => {
  return useMutation({
    mutationFn: async ({ dagId, task }: { dagId: string; task: AirflowTaskDto }) => {
      const res = await ScheduledTasksApi.createTask(dagId, task);
      return defaultResponseHandler(res);
    },
    onSuccess(response, { dagId }) {
      queryClient.setQueryData(
        [QueryKey.SCHEDULED_TASK_DAG, dagId],
        (oldQueryData: AirflowDag) =>
          oldQueryData && { ...oldQueryData, tasks: [...oldQueryData.tasks, response?.taskDto] }
      );
      queryClient.setQueryData(
        [QueryKey.SCHEDULED_TASK_DAGS],
        (oldQueryData: AirflowDag[]) =>
          oldQueryData &&
          oldQueryData.map(dag =>
            dag.dagId !== dagId ? dag : { ...dag, tasks: [...dag.tasks, response?.taskDto] }
          )
      );
      if (response && response.deployed) {
        successToaster(T("alert.task-created-successfully", { taskId: response.taskDto?.id.taskId }));
      } else {
        warningToaster(T("alert.task-created-but-not-deployed", { taskId: response?.taskDto?.id.taskId }));
      }
    },
    onError: error => {
      errorToaster(error.message);
    },
  });
};

export const useUpdateTask = () => {
  return useMutation({
    mutationFn: async ({ config, run = false }: { config: ScheduledTaskDTO; run: boolean }) => {
      const res = await ScheduledTasksApi.saveAirflowTaskConfig(config, run);
      return defaultResponseHandler(res);
    },
    onSuccess(response, { run }) {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SCHEDULED_TASK_DAG, response?.parentId],
      });

      if (run) {
        successToaster(T("alert.task-successfully-saved-and-started"));
      } else {
        successToaster(T("alert.task-configuration-saved-successfully"));
      }
    },
    onError: error => {
      errorToaster(`${T("error.error-saving-task-configuration")} ${error.message}`);
    },
  });
};

export const useDeleteTask = () => {
  return useMutation({
    mutationFn: async ({ dagId, taskId }: AirflowTaskId) => {
      const res = await ScheduledTasksApi.deleteTask(dagId, taskId);
      return defaultResponseHandler(res);
    },
    onSuccess(_, { dagId, taskId }) {
      queryClient.setQueryData(
        [QueryKey.SCHEDULED_TASK_DAG, dagId],
        (oldQueryData: AirflowDag) =>
          oldQueryData && {
            ...oldQueryData,
            tasks: oldQueryData.tasks.filter(task => task.id.taskId !== taskId),
          }
      );
      queryClient.setQueryData(
        [QueryKey.SCHEDULED_TASK_DAGS],
        (oldQueryData: AirflowDag[]) =>
          oldQueryData &&
          oldQueryData.map(dag =>
            dag.dagId !== dagId ? dag : { ...dag, tasks: dag.tasks.filter(task => task.id.taskId !== taskId) }
          )
      );
      successToaster(T("alert.task-deleted-successfully", { taskId }));
    },
  });
};

export const useGetTasksUsage = () => {
  return useMutation({
    mutationFn: async (usage: AirflowSearchQuery) => {
      const res = await ScheduledTasksApi.getTasksUsage(usage);
      return defaultResponseHandler(res);
    },
  });
};
